import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";

export const ConfirmDialog = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel = () => {},
  confirmText = "Confirm",
  cancelText = "Cancel",
  isDestructive = false,
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[24px] bg-white p-6 text-left align-middle shadow-2xl transition-all border border-slate-100">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-black leading-6 text-slate-900 tracking-tight"
                >
                  {title}
                </Dialog.Title>
                <div className="mt-3">
                  <p className="text-sm text-slate-500 font-medium leading-relaxed">
                    {message}
                  </p>
                </div>

                <div className="mt-8 flex gap-3 justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-slate-600 hover:bg-slate-50 focus:outline-none transition-all"
                    onClick={onCancel}
                  >
                    {cancelText}
                  </button>
                  <button
                    type="button"
                    className={`inline-flex justify-center rounded-xl border border-transparent px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white shadow-md focus:outline-none transition-all ${
                      isDestructive
                        ? "bg-rose-500 hover:bg-rose-600 shadow-rose-500/20"
                        : "bg-slate-900 hover:bg-slate-800 shadow-slate-900/20"
                    }`}
                    onClick={onConfirm}
                  >
                    {confirmText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const PromptDialog = ({
  isOpen,
  title,
  message,
  defaultValue = "",
  placeholder = "",
  onConfirm,
  onCancel = () => {},
  confirmText = "Submit",
  cancelText = "Cancel",
  isDestructive = false,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);

  useEffect(() => {
    if (isOpen) {
      setInputValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-[24px] bg-white p-6 text-left align-middle shadow-2xl transition-all border border-slate-100">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-black leading-6 text-slate-900 tracking-tight"
                >
                  {title}
                </Dialog.Title>
                <div className="mt-3">
                  <p className="text-sm text-slate-500 font-medium leading-relaxed">
                    {message}
                  </p>
                </div>
                <div className="mt-4">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={placeholder}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                    autoFocus
                  />
                </div>

                <div className="mt-8 flex gap-3 justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-slate-600 hover:bg-slate-50 focus:outline-none transition-all"
                    onClick={onCancel}
                  >
                    {cancelText}
                  </button>
                  <button
                    type="button"
                    className={`inline-flex justify-center rounded-xl border border-transparent px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white shadow-md focus:outline-none transition-all ${
                      isDestructive
                        ? "bg-rose-500 hover:bg-rose-600 shadow-rose-500/20"
                        : "bg-slate-900 hover:bg-slate-800 shadow-slate-900/20"
                    }`}
                    onClick={() => onConfirm(inputValue)}
                  >
                    {confirmText}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
